import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://startpage.thinkbeyond.ai/api';

axios.defaults.withCredentials = true;

export interface SharedContent {
  id: number;
  content_type: 'tab';
  title: string;
  description?: string;
  content_data: any;
  category?: string;
  tags: string[];
  is_public: boolean;
  is_featured: boolean;
  usage_count: number;
  rating_sum: number;
  rating_count: number;
  average_rating: number;
  created_by: number;
  creator_email: string;
  creator_username: string;
  created_at: string;
  updated_at: string;
}

export interface ShareContentRequest {
  contentType: 'tab';
  contentId?: string;
  title: string;
  description?: string;
  category?: string;
  tags?: string[];
  isPublic?: boolean;
}

export interface SharingStats {
  total_shared?: number;
  total_downloads?: number;
  average_rating?: number;
  total_imported?: number;
  total_content?: number;
  total_creators?: number;
}


const sharingApi = {
  // Share content (tiles only for this implementation)
  async shareContent(data: ShareContentRequest) {
    try {
      const response = await axios.post(`${API_BASE}/sharing/share`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to share content');
    }
  },

  // Browse shared content
  async browseSharedContent(params: {
    contentType?: string;
    category?: string;
    search?: string;
    sortBy?: 'newest' | 'popular' | 'rating';
    page?: number;
    limit?: number;
    featured?: boolean;
  } = {}) {
    try {
      const response = await axios.get(`${API_BASE}/sharing/browse`, { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to browse shared content');
    }
  },

  // Get specific shared content
  async getSharedContent(id: number) {
    try {
      const response = await axios.get(`${API_BASE}/sharing/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get shared content');
    }
  },

  // Import shared content
  async importSharedContent(id: number, data: { rating?: number; targetGroupId?: string; replaceAll?: boolean }) {
    try {
      const response = await axios.post(`${API_BASE}/sharing/${id}/import`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to import content');
    }
  },

  // Rate shared content
  async rateSharedContent(id: number, rating: number) {
    try {
      const response = await axios.post(`${API_BASE}/sharing/${id}/rate`, { rating });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to rate content');
    }
  },

  // Get user's shared content
  async getUserSharedContent(includePrivate: boolean = true) {
    try {
      const response = await axios.get(`${API_BASE}/sharing/user/shared`, {
        params: { includePrivate }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get user shared content');
    }
  },

  // Get user's imported content
  async getUserImportedContent(contentType?: string) {
    try {
      const response = await axios.get(`${API_BASE}/sharing/user/imported`, {
        params: { contentType }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get imported content');
    }
  },

  // Update shared content
  async updateSharedContent(id: number, updates: Partial<ShareContentRequest>) {
    try {
      const response = await axios.put(`${API_BASE}/sharing/${id}`, updates);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update shared content');
    }
  },

  // Delete shared content
  async deleteSharedContent(id: number) {
    try {
      const response = await axios.delete(`${API_BASE}/sharing/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete shared content');
    }
  },

  // Get popular categories
  async getPopularCategories() {
    try {
      const response = await axios.get(`${API_BASE}/sharing/categories`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get categories');
    }
  },

  // Get sharing statistics
  async getSharingStats(global: boolean = false): Promise<{ success: boolean; data: SharingStats }> {
    try {
      const response = await axios.get(`${API_BASE}/sharing/stats`, {
        params: { global }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get sharing stats');
    }
  },

  // Permission management
  async grantPermission(id: number, userId: number, permissionType: 'view' | 'import' | 'edit') {
    try {
      const response = await axios.post(`${API_BASE}/sharing/${id}/permissions/grant`, {
        userId,
        permissionType
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to grant permission');
    }
  },

  async revokePermission(id: number, userId: number, permissionType: 'view' | 'import' | 'edit') {
    try {
      const response = await axios.post(`${API_BASE}/sharing/${id}/permissions/revoke`, {
        userId,
        permissionType
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to revoke permission');
    }
  },

  async getContentPermissions(id: number) {
    try {
      const response = await axios.get(`${API_BASE}/sharing/${id}/permissions`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get permissions');
    }
  },

  // Share content with team
  async shareWithTeam(data: {
    teamId: number;
    contentId: string;
    title: string;
    description?: string;
    category?: string;
    tags?: string[];
  }) {
    try {
      const response = await axios.post(`${API_BASE}/sharing/share-with-team`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to share with team');
    }
  },

  // Share content with specific emails
  async shareWithEmails(data: {
    emails: string[];
    contentId: string;
    title: string;
    description?: string;
    category?: string;
    tags?: string[];
  }) {
    try {
      const response = await axios.post(`${API_BASE}/sharing/share-with-emails`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to share with emails');
    }
  },

  // Get who a tab is shared with
  async getTabSharedWith(tabId: string) {
    try {
      const response = await axios.get(`${API_BASE}/sharing/tab/${tabId}/shared-with`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get shared recipients');
    }
  },

  // Remove specific user from tab sharing
  async removeUserFromTabSharing(tabId: string, userEmail: string) {
    try {
      const response = await axios.delete(`${API_BASE}/sharing/tab/${tabId}/remove-user`, {
        data: { userEmail }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to remove user from tab sharing');
    }
  }
};

export default sharingApi;